import React, { useState, type JSX } from 'react';

import { DeviceRosterOverlay } from './DeviceRosterOverlay';
import {
  TABLE_BODY_CLASS,
  TABLE_HEAD_CELL_CLASS,
  TABLE_HEAD_STICKY_CLASS,
  VALIDATION_REPORT_FIRST_CELL_FAIL_CLASS,
  VALIDATION_REPORT_FIRST_CELL_OK_CLASS,
  VALIDATION_REPORT_ROW_FAIL_CLASS,
  VALIDATION_REPORT_ROW_OK_CLASS,
  VALIDATION_REPORT_TABLE_CLASS,
} from './tableDesign';
import { ValidationFieldCellMark } from './RaceValidationReportTable';
import type { AthleteAssignmentReportRow } from './types/ValidationReportTypes';

type TableProps = {
  rows: AthleteAssignmentReportRow[];
  emptyMessage?: string;
};

/** Athlete assignment checks for race stage transitions (mirrors RaceValidationReportTable styling). */
export function AthleteAssignmentReportTable(props: TableProps): JSX.Element {
  const { rows, emptyMessage = 'No athletes on the athlete list.' } = props;
  const [rosterDeviceId, setRosterDeviceId] = useState<string | null>(null);

  if (rows.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-gray-200 bg-white px-4 py-6 text-center text-xs text-gray-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
        {emptyMessage}
      </p>
    );
  }

  return (
    <>
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
      <table className={VALIDATION_REPORT_TABLE_CLASS}>
        <thead className={TABLE_HEAD_STICKY_CLASS}>
          <tr>
            <th className={`${TABLE_HEAD_CELL_CLASS} min-w-[6rem] pl-7`}>Athlete</th>
            <th className={`${TABLE_HEAD_CELL_CLASS} min-w-[5rem]`}>Athlete ID</th>
            <th className={`${TABLE_HEAD_CELL_CLASS} min-w-[4rem]`}>Race No</th>
            <th className={`${TABLE_HEAD_CELL_CLASS} min-w-[8rem]`}>Rear device ID</th>
            <th className={`${TABLE_HEAD_CELL_CLASS} min-w-[8rem]`}>Front device ID</th>
            <th
              className={`${TABLE_HEAD_CELL_CLASS} text-center`}
              title="Rear device must be assigned before Distribution"
            >
              Rear
            </th>
            <th
              className={`${TABLE_HEAD_CELL_CLASS} text-center`}
              title="Front device should be assigned for complete athlete pairing"
            >
              Front
            </th>
            <th className={TABLE_HEAD_CELL_CLASS}>Notes</th>
          </tr>
        </thead>
        <tbody className={TABLE_BODY_CLASS}>
          {rows.map((row) => {
            const rowWarn = row.rear.result === 'fail' || row.front.result === 'fail';
            return (
              <tr
                key={`${row.athleteId}#${row.athleteRaceNo}`}
                className={rowWarn ? VALIDATION_REPORT_ROW_FAIL_CLASS : VALIDATION_REPORT_ROW_OK_CLASS}
              >
                <td
                  className={[
                    'py-2 pl-3 pr-4 text-xs text-gray-900 dark:text-slate-100',
                    rowWarn ? VALIDATION_REPORT_FIRST_CELL_FAIL_CLASS : VALIDATION_REPORT_FIRST_CELL_OK_CLASS,
                  ].join(' ')}
                >
                  {row.athleteName || '—'}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-gray-900 dark:text-slate-100">{row.athleteId}</td>
                <td className="px-4 py-2 font-mono text-xs text-gray-900 dark:text-slate-100">{row.athleteRaceNo}</td>
                <td className="px-4 py-2 font-mono text-xs text-gray-900 dark:text-slate-100">
                  {row.rearDeviceId.trim() ? (
                    <button
                      type="button"
                      className="hover:underline cursor-pointer"
                      onClick={() => setRosterDeviceId(row.rearDeviceId.trim())}
                    >
                      {row.rearDeviceId.trim()}
                    </button>
                  ) : '—'}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-gray-700 dark:text-slate-300">
                  {row.frontDeviceId.trim() ? (
                    <button
                      type="button"
                      className="hover:underline cursor-pointer"
                      onClick={() => setRosterDeviceId(row.frontDeviceId.trim())}
                    >
                      {row.frontDeviceId.trim()}
                    </button>
                  ) : '—'}
                </td>
                <td className="px-4 py-2 text-center align-middle">
                  <ValidationFieldCellMark cell={row.rear} />
                </td>
                <td className="px-4 py-2 text-center align-middle">
                  <ValidationFieldCellMark cell={row.front} />
                </td>
                <td className="max-w-md px-4 py-2 text-xs text-gray-800 dark:text-slate-200">
                  {row.notes ? (
                    <span className="text-red-900 dark:text-red-300">{row.notes}</span>
                  ) : (
                    <span className="text-emerald-800 dark:text-emerald-300">Rear and front devices assigned</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    <DeviceRosterOverlay deviceId={rosterDeviceId} onClose={() => setRosterDeviceId(null)} />
    </>
  );
}
